import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey, TimelineClassKey } from "assets/jss/common-styles";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import LibUtils from "lib/LibUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedContentUpdate, FeedTopicCategory, FeedTopicFilter, FeedTopicOrderBy } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { FeedMessageTypes } from "store/feed/windowMessages";
import { ApplicationState } from "store/rootReducer";
import CardManager from "views/Timeline/Components/CardManager";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    orderByMenuAnchorEl: any;
    dialog: any;
}

interface IProps {
    filter: FeedTopicFilter;
    orderBy: FeedTopicOrderBy;

    fullView: boolean;
    onRef?: (ref: FeedTopicPublicationsComponent) => void;
    onClearFilter?: () => void;
}

interface IDispatchProps {
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onExpandPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemovePublication: (uid: string, publicationId: string, publicationType: PublicationType) => Promise<boolean>;
    onFeedReset: () => void;
    onUpdateFeedData: (newItemDto: FeedContentUpdate) => void;
    onRemoveFromFeed: (uid: string, publicationId: string) => void;
    onGetFeed: (filter: FeedTopicFilter, orderBy: FeedTopicOrderBy, startIndex: number, endIndex: number) => Promise<any>;
    executeFeedMessage: (messageType: FeedMessageTypes) => void;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class FeedTopicPublicationsComponent extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            orderByMenuAnchorEl: null,
            dialog: null
        };
    }


    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    private isFiltered() {
        const { filter } = this.props;
        return !LibUtils.isAllPropertiesWithValue(filter, null, ["topicId", "category"]);
    }


    ///
    private handleResetFilter = () => {
        this.props.onClearFilter();
    };

    ///
    public componentDidMount() {
        if (this.props.onRef) this.props.onRef(this);
    }

    ///
    public componentWillUnmount() {
        if (this.props.onRef) this.props.onRef(null);
        this.feedReset();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.filter != this.props.filter || prevProps.orderBy != this.props.orderBy) {
            this.feedReset();
        }
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, totalRecords, loading }, filter: { category }, snackbar } = this.props;

        return (
            <>
                {this.state.dialog}

                {
                    totalRecords > 0 &&
                    <MuiContainer margin="normal">
                        <CountFilter
                            count={totalRecords}
                            filtered={this.isFiltered()}
                            resource="Common.Feed.Filter.PublicationResultText"
                            filteredResource="Common.Feed.Filter.PublicationFilteredResultText"
                            onResetFilter={() => this.handleResetFilter()}
                        />
                    </MuiContainer>
                }


                <CardManager
                    classes={classes}
                    feedType={FeedType.Topic}
                    feed={feed}
                    snackbar={snackbar}
                    onReloadPublication={this.props.onReloadPublication}
                    onRemovePublication={this.props.onRemovePublication}
                    onExpandPublication={this.props.onExpandPublication}
                    onUpdatePublication={this.props.onUpdateFeedData}
                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                    onGetFeed={(startIndex: number, endIndex: number) => this.props.onGetFeed(this.props.filter, this.props.orderBy, startIndex, endIndex)}
                    onFeedReset={() => this.props.onFeedReset()}
                />

                {
                    lastError != null
                        ? <RequestErrorReport error={lastError} />
                        : totalRecords == 0 && !loading && !this.isFiltered()
                            ? (
                                <MuiMessageBox
                                    variant="info"
                                    message={
                                        category == FeedTopicCategory.Read
                                            ? this.props.t("Common.Feed.Topic.PublicationsNotFound")
                                            : this.props.t("Common.Feed.Topic.QuestionsNotFound")
                                    }
                                />
                            )
                            : totalRecords == 0 && !loading && this.isFiltered()
                                ? (
                                    <MuiMessageBox
                                        variant="warning"
                                        action={<ButtonResetFilter color="warning" onClick={this.handleResetFilter} />}
                                        message={this.props.t("Common.Feed.Filter.PublicationFilteredResultText_zero")}
                                    />
                                )
                                : null
                }
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.TopicFeedAction(ownProps.filter.topicId);

    return {
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),

        onGetFeed: (filter, orderBy, startIndex, endIndex) => dispatch(action.getFeed(filter, orderBy, startIndex, endIndex)),

        executeFeedMessage: (messageType) => action.executeFeedMessage(messageType, dispatch)
    };
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(FeedTopicPublicationsComponent)));
